/**
 * EmploymentTypes — migrated from mockData to live svdy-platform API.
 *
 * Backend shape: EmploymentTypeResponse = { id, name, code, description,
 * worker_count, is_default, created_at }. Note the backend does NOT
 * carry an `is_active` field — the toggle here maps to `is_default`
 * (which flag gets auto-selected when creating a new worker).
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import { useTranslation } from 'react-i18next';

import { CodeCell } from '../../../components/organization/CodeCell';
import { CodeField, isCodeValid } from '../../../components/organization/CodeField';

import {
  ApiError,
  organizationApi,
  type Schema,
} from '../../../api';
import { ConfirmDialog } from '../../../components/primitives';
import { employmentTypeDisplayName } from './employmentTypeDisplay';
import { translateApiError } from '../../../i18n/translateApiError';

type EmploymentType = Schema<'EmploymentTypeResponse'>;
type EmploymentTypeCreate = Schema<'EmploymentTypeCreate'>;
type EmploymentTypeUpdate = Schema<'EmploymentTypeUpdate'>;

type FormState = Partial<EmploymentType>;

const EmploymentTypes: React.FC = () => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<EmploymentType[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [current, setCurrent] = useState<FormState | null>(null);
  const [toDelete, setToDelete] = useState<EmploymentType | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterText, setFilterText] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const data = await organizationApi.employmentTypesApi.list();
      setRows(data);
      setError(null);
    } catch (e) {
      setError(
        e instanceof ApiError ? translateApiError(e, t) : t('org:employmentTypes.toast.loadFailed'),
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const displayName = useCallback(
    (row: { name: string; code?: string | null }) =>
      employmentTypeDisplayName(row, t),
    [t],
  );

  const summaryStats = useMemo(() => {
    const total = rows.length;
    const defaultCount = rows.filter((r) => r.is_default).length;
    const totalWorkers = rows.reduce(
      (sum, r) => sum + (r.worker_count ?? 0),
      0,
    );
    return { total, defaultCount, totalWorkers };
  }, [rows]);

  const summaryCards = useMemo(
    () => [
      {
        id: 'total',
        value: summaryStats.total,
        title: t('org:employmentTypes.stats.total'),
        description: t('org:employmentTypes.stats.totalDescription'),
        avatar: {
          icon: <BadgeOutlinedIcon />,
          bgcolor: 'primary.light',
          color: 'primary.main',
        },
      },
      {
        id: 'default',
        value: summaryStats.defaultCount,
        title: t('org:employmentTypes.stats.defaultCount'),
        description: t('org:employmentTypes.stats.defaultDescription'),
        avatar: {
          icon: <StarOutlineIcon />,
          bgcolor: 'warning.light',
          color: 'warning.dark',
        },
      },
      {
        id: 'workers',
        value: summaryStats.totalWorkers,
        title: t('org:employmentTypes.stats.totalHeadcount'),
        description: t('org:employmentTypes.stats.totalHeadcountDescription'),
        avatar: {
          icon: <PeopleAltOutlinedIcon />,
          bgcolor: 'info.light',
          color: 'info.main',
        },
      },
    ],
    [summaryStats, t],
  );

  const filtered = useMemo(() => {
    const needle = filterText.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter(
      (r) =>
        displayName(r).toLowerCase().includes(needle) ||
        (r.code ?? '').toLowerCase().includes(needle) ||
        (r.description ?? '').toLowerCase().includes(needle),
    );
  }, [rows, filterText, displayName]);

  const paginated = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage],
  );

  // -------------------------------------------------------------------------
  // CRUD
  // -------------------------------------------------------------------------

  const validateForm = (): boolean => {
    if (!current) return false;
    const errs: Record<string, string> = {};
    if (!current.name?.trim()) errs.name = t('org:employmentTypes.fields.nameRequired');
    // ADR-043 D6. The field shows the specific rule under itself; this stops the
    // submit. The server refuses it too, but making the admin wait for a round
    // trip to learn about a space in a code is a worse experience for the same
    // outcome.
    if (!isCodeValid(current?.code || '')) {
      errs.code = t('org:shared.codeField.label');
    }
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleOpenAddDialog = () => {
    setCurrent({ is_default: false });
    setFormErrors({});
    setOpenFormDialog(true);
  };

  const handleOpenEditDialog = (row: EmploymentType) => {
    setCurrent({ ...row });
    setFormErrors({});
    setOpenFormDialog(true);
  };

  const handleCloseFormDialog = () => {
    setOpenFormDialog(false);
    setCurrent(null);
    setFormErrors({});
  };

  const handleSave = async () => {
    if (!validateForm() || !current) return;
    setSaving(true);
    try {
      if (current.id) {
        const update: EmploymentTypeUpdate = {
          name: current.name ?? null,
          code: current.code ?? null,
          description: current.description ?? null,
        };
        await organizationApi.employmentTypesApi.update(current.id, update);
        setToast(t('org:employmentTypes.toast.updated', {
          name: displayName({ name: current.name!, code: current.code }),
        }));
      } else {
        const create: EmploymentTypeCreate = {
          name: current.name!,
          code: current.code ?? null,
          description: current.description ?? null,
        };
        await organizationApi.employmentTypesApi.create(create);
        setToast(t('org:employmentTypes.toast.created', {
          name: displayName(create),
        }));
      }
      handleCloseFormDialog();
      await fetchAll();
    } catch (e) {
      const message =
        e instanceof ApiError ? translateApiError(e, t) : t('org:employmentTypes.toast.saveFailed');
      setFormErrors((prev) => ({ ...prev, _global: message }));
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDeleteDialog = (row: EmploymentType) => {
    setToDelete(row);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setToDelete(null);
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    setSaving(true);
    try {
      await organizationApi.employmentTypesApi.delete(toDelete.id);
      setSelectedIds((prev) => prev.filter((id) => id !== toDelete.id));
      // Dialog auto-closes on success; still clear row selection.
      handleCloseDeleteDialog();
      setToast(t('org:employmentTypes.toast.deleted', {
        name: displayName(toDelete),
      }));
      await fetchAll();
    } catch (e) {
      const message =
        e instanceof ApiError
          ? translateApiError(e, t)
          : t('org:employmentTypes.toast.deleteFailed');
      setToast(message);
      // Rethrow so ConfirmDialog keeps itself open for retry.
      throw e;
    } finally {
      setSaving(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) {
      setToast(t('org:employmentTypes.toast.selectOneDelete'));
      return;
    }
    setSaving(true);
    const failures: string[] = [];
    for (const id of selectedIds) {
      try {
        await organizationApi.employmentTypesApi.delete(id);
      } catch {
        failures.push(id);
      }
    }
    setSelectedIds([]);
    await fetchAll();
    setSaving(false);
    if (failures.length === 0) {
      setToast(t('org:employmentTypes.toast.bulkDeleted', { count: selectedIds.length }));
    } else {
      setToast(
        t('org:employmentTypes.toast.bulkDeletedPartial', {
          ok: selectedIds.length - failures.length,
          failed: failures.length,
        }),
      );
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleFormChange = (event: any) => {
    const { name, value, type, checked } = event.target;
    setCurrent((prev) =>
      prev
        ? {
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
          }
        : null,
    );
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  // -------------------------------------------------------------------------
  // Row selection / pagination
  // -------------------------------------------------------------------------

  const handleSelectRowClick = (
    _event: React.SyntheticEvent,
    id: string,
  ) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const isSelected = (id: string) => selectedIds.includes(id);

  const handleChangePage = (_event: unknown, newPage: number) =>
    setPage(newPage);
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterText(event.target.value);
    setPage(0);
  };
  const handleSelectAllClick = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setSelectedIds(event.target.checked ? filtered.map((r) => r.id) : []);
  };

  // -------------------------------------------------------------------------
  // Render guards
  // -------------------------------------------------------------------------

  if (loading && rows.length === 0) {
    return (
      <Container
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '80vh',
        }}
      >
        <CircularProgress />
      </Container>
    );
  }

  if (error && rows.length === 0) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={fetchAll}>
              {t('common:actions.retry')}
            </Button>
          }
        >
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        py: '12px',
      }}
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          mb: 1.5,
          gap: 1.5,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              fontSize: '1.125rem',
              color: 'text.primary',
            }}
          >
            {t('org:employmentTypes.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('org:employmentTypes.subtitle')}
          </Typography>
        </Box>
        <Box>
          <Tooltip title={t('common:actions.refresh')}>
            <IconButton
              size="small"
              onClick={fetchAll}
              sx={{
                mr: 1,
                border: '1px solid rgba(0,0,0,0.2)',
                borderRadius: 1,
                bgcolor: '#ffffff',
                '&:hover': { bgcolor: '#f5f5f5' },
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAddDialog}
            size="small"
            sx={{
              mr: 1,
              borderRadius: 1,
              textTransform: 'none',
              fontSize: '0.875rem',
            }}
          >
            {t('org:employmentTypes.addButton')}
          </Button>
          <Button
            variant="outlined"
            onClick={handleBulkDelete}
            size="small"
            color="error"
            disabled={selectedIds.length === 0 || saving}
            sx={{
              borderRadius: 1,
              textTransform: 'none',
              fontSize: '0.875rem',
              bgcolor: '#ffffff',
              '&:hover': { bgcolor: '#ffffff' },
            }}
          >
            {t('org:shared.bulkDelete')}
          </Button>
        </Box>
      </Box>

      {/* Compact stats — 13" laptop friendly */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2.5, flexWrap: 'wrap' }}>
        {summaryCards.flatMap((card, i) => [
          i > 0 ? <Typography key={`sep-${card.id}`} variant="caption" color="text.disabled">·</Typography> : null,
          <Typography key={card.id} variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {React.cloneElement(card.avatar.icon, { sx: { fontSize: 14, color: card.avatar.color } })}
            <strong>{card.value}</strong> {card.title}
          </Typography>,
        ]).filter(Boolean)}
      </Stack>

      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <TextField
          variant="outlined"
          label={t('org:employmentTypes.filterLabel')}
          value={filterText}
          onChange={handleFilterChange}
          size="small"
          sx={{ width: '400px', maxWidth: '100%', mr: 2 }}
        />
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filtered.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Box>

      {filtered.length === 0 && !loading ? (
        <Paper sx={{ p: 3, textAlign: 'center', width: '100%' }}>
          <Typography variant="subtitle1" gutterBottom>
            {filterText
              ? t('org:employmentTypes.empty.noMatches')
              : t('org:employmentTypes.empty.noResults')}
          </Typography>
          {!filterText && (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleOpenAddDialog}
              sx={{ mt: 2 }}
            >
              {t('org:employmentTypes.addButton')}
            </Button>
          )}
        </Paper>
      ) : (
        <TableContainer
          component={Paper}
          sx={{ width: '100%', borderRadius: 2, overflow: 'hidden' }}
        >
          <Table sx={{ minWidth: 650 }} aria-label={t('org:employmentTypes.tableAria')}>
            <TableHead
              sx={{
                bgcolor: 'grey.100',
                '& .MuiTableCell-root': { fontWeight: 600 },
              }}
            >
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      selectedIds.length > 0 &&
                      selectedIds.length < filtered.length
                    }
                    checked={
                      filtered.length > 0 &&
                      selectedIds.length === filtered.length
                    }
                    onChange={handleSelectAllClick}
                  />
                </TableCell>
                <TableCell>{t('org:employmentTypes.table.name')}</TableCell>
                <TableCell>{t('org:employmentTypes.table.code')}</TableCell>
                <TableCell>{t('org:employmentTypes.table.description')}</TableCell>
                <TableCell align="right">{t('org:employmentTypes.table.workers')}</TableCell>
                <TableCell>{t('org:employmentTypes.table.default')}</TableCell>
                <TableCell align="center">{t('org:employmentTypes.table.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.map((r) => {
                const selected = isSelected(r.id);
                return (
                  <TableRow
                    hover
                    key={r.id}
                    selected={selected}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selected}
                        onChange={(event) => handleSelectRowClick(event, r.id)}
                        inputProps={{ 'aria-label': displayName(r) }}
                      />
                    </TableCell>
                    <TableCell>{displayName(r)}</TableCell>
                    <TableCell>
                      {/* No `codeSource`: ADR-043 S1 scoped `code_source` to departments and
                          positions, the two entities the Workday sync writes. Reading a
                          field that does not exist would be an expression that lies. */}
                      <CodeCell code={r.code} />
                    </TableCell>
                    <TableCell
                      sx={{
                        maxWidth: 300,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {r.description || '-'}
                    </TableCell>
                    <TableCell align="right">{r.worker_count ?? 0}</TableCell>
                    <TableCell>{r.is_default ? t('org:employmentTypes.table.defaultYes') : '-'}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        aria-label={t('org:shared.table.edit')}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenEditDialog(r);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        aria-label={t('common:actions.delete')}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenDeleteDialog(r);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={openFormDialog}
        onClose={handleCloseFormDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ m: 0, py: 2 }}>
          {current?.id ? t('org:employmentTypes.editDialogTitle') : t('org:employmentTypes.addDialogTitle')}
        </DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          {formErrors._global && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formErrors._global}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            name="name"
            label={t('org:employmentTypes.fields.name')}
            fullWidth
            variant="outlined"
            value={current?.name || ''}
            onChange={handleFormChange}
            required
            error={!!formErrors.name}
            helperText={formErrors.name}
            sx={{ mb: 2 }}
          />
          <CodeField
            value={current?.code || ''}
            onChange={(code) =>
              setCurrent((prev) => (prev ? { ...prev, code } : prev))
            }
                    />
          <TextField
            margin="dense"
            name="description"
            label={t('org:employmentTypes.fields.description')}
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={current?.description || ''}
            onChange={handleFormChange}
            sx={{ mb: 2 }}
          />
          {current?.is_default !== undefined && current?.id && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!current?.is_default}
                  disabled
                  color="primary"
                />
              }
              label={t('org:employmentTypes.fields.defaultReadOnly')}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ py: 2 }}>
          <Button onClick={handleCloseFormDialog} color="inherit">
            {t('common:actions.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={saving}
          >
            {saving ? (
              <CircularProgress size={24} />
            ) : current?.id ? (
              t('org:shared.saveChanges')
            ) : (
              t('org:employmentTypes.createSubmit')
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={openDeleteDialog}
        tone="danger"
        title={t('org:employmentTypes.deleteTitle')}
        description={t('org:employmentTypes.deleteDescription')}
        subject={
          toDelete && (
            <Box sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>
              {displayName(toDelete)}
            </Box>
          )
        }
        confirmLabel={t('common:actions.delete')}
        confirmLoadingLabel={t('common:status.deleting')}
        onConfirm={handleDelete}
        onClose={handleCloseDeleteDialog}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity="info"
          onClose={() => setToast(null)}
          sx={{ width: '100%' }}
        >
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EmploymentTypes;
